import "../assets/css/calendar.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faCalendarAlt,
  faClock,
  faAlignLeft,
} from "@fortawesome/free-solid-svg-icons";
import { EventItem } from "./EventAndCalendar";
import { readableDate } from "../utilities/common";
import { isSameDay, format } from "date-fns";

const EventList = ({
  events,
  selectedDate: value,
  onClick,
}: {
  events: EventItem[];
  selectedDate: Date;
  onClick: any;
}) => {
  function todayEvents() {
    return (
      events &&
      events.filter(
        (event) =>
          event?.start_date && isSameDay(new Date(event.start_date), value),
      )
    );
  }

  const currentEvents = todayEvents();
  const formattedDate = format(value, "MMMM dd, yyyy");

  return (
    <div className="w-full bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {/* Top Header Panel */}
      <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#0096D1]/10 text-[#0096D1] flex items-center justify-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-lg" />
          </div>
          <div>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
              Agenda Schedule
            </span>
            <h1 className="text-lg font-extrabold text-slate-800 tracking-tight">
              {formattedDate}
            </h1>
          </div>
        </div>

        <button
          onClick={onClick.create}
          className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 bg-[#0096D1] hover:bg-[#007ba8] text-white text-xs font-bold shadow-sm transform active:scale-95 transition-all w-full sm:w-auto"
        >
          <span>Add Event</span>
          <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
        </button>
      </div>

      {/* Main Container Content */}
      <div className="p-4 h-[55vh] overflow-y-auto register-scroll bg-white divide-y divide-slate-100">
        {currentEvents && currentEvents.length > 0 ? (
          currentEvents.map((item, index) => {
            const start = readableDate(item.start_date);
            const end = readableDate(item.end_date);
            const standardOneDay = item.start_date === item.end_date;

            return (
              <div
                key={index}
                className="group flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-5 first:pt-2 last:pb-2 hover:bg-slate-50/40 rounded-xl transition-colors duration-150"
              >
                {/* Left: Metadata Details Block */}
                <div className="flex-1 space-y-2.5 min-w-0">
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold text-[#0096D1] uppercase tracking-wider block">
                      Event Title
                    </span>
                    <h2 className="text-[15px] font-bold text-slate-800 capitalize tracking-tight group-hover:text-[#0096D1] transition-colors duration-150 truncate">
                      {item.title}
                    </h2>
                  </div>

                  {item.description && (
                    <div className="space-y-0.5">
                      <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                        <FontAwesomeIcon
                          icon={faAlignLeft}
                          className="text-[9px]"
                        />{" "}
                        Description
                      </span>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed max-w-2xl line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                  )}

                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 font-medium text-xs border border-slate-200/40">
                    <FontAwesomeIcon
                      icon={faClock}
                      className="text-[10px] text-slate-400"
                    />
                    <span>
                      {start} {!standardOneDay && ` to ${end}`}
                    </span>
                  </div>
                </div>

                {/* Right: Operational Interactive Control Track */}
                <div className="flex items-center gap-2 w-full md:w-auto justify-end border-t md:border-t-0 border-slate-100 pt-3 md:pt-0">
                  <button
                    onClick={() => onClick.edit(item)}
                    className="inline-flex items-center justify-center rounded-xl px-4 py-2 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold shadow-sm hover:bg-slate-100 active:scale-95 transition-all"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onClick.delete(item.id)}
                    className="inline-flex items-center justify-center rounded-xl px-4 py-2 bg-rose-50 border border-rose-100 text-rose-600 text-xs font-bold hover:bg-rose-100 active:scale-95 transition-all"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center p-6">
            <div className="h-12 w-12 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 mb-3">
              📅
            </div>
            <p className="text-slate-400 text-sm font-medium">
              No active entries planned for this operational calendar view...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventList;
